/**
 * ============================================================================
 * SHIPPING SETTINGS SERVICE
 * ============================================================================
 * 
 * Serviço para buscar configurações globais de frete do Supabase.
 * Integra painel administrativo com a loja.
 */

import { supabase } from '../lib/supabase';

export interface ShippingSettings {
  id: string;
  free_shipping_threshold: number;  // Valor para frete grátis (CHF)
  max_weight: number;                // Peso máximo (kg)
  max_dimensions: string;            // Dimensões máximas (ex: "100x100x100")
  processing_days: number;           // Dias de processamento
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface DimensionsValidation {
  length: number;
  width: number;
  height: number;
  isValid: boolean;
  maxDimensions: string;
}

/**
 * Cache em memória para evitar múltiplas requisições
 */
let cachedSettings: ShippingSettings | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

/**
 * Busca configurações de frete do Supabase
 * @returns Configurações ativas ou defaults
 */
export const getShippingSettings = async (): Promise<ShippingSettings> => {
  try {
    // Verificar cache
    const now = Date.now();
    if (cachedSettings && (now - cacheTimestamp) < CACHE_DURATION) {
      return cachedSettings;
    }

    // Buscar do banco
    const { data, error } = await supabase
      .from('shipping_settings')
      .select('*')
      .eq('active', true)
      .single();

    if (error) {
      console.error('❌ Erro ao buscar shipping settings:', error);
      return getDefaultSettings();
    }

    if (!data) {
      console.warn('⚠️ Nenhuma configuração encontrada, usando defaults');
      return getDefaultSettings();
    }

    // Atualizar cache
    cachedSettings = data;
    cacheTimestamp = now;

    return data;
  } catch (error) {
    console.error('❌ Erro ao buscar shipping settings:', error);
    return getDefaultSettings();
  }
};

/**
 * Retorna configurações padrão (fallback)
 */
const getDefaultSettings = (): ShippingSettings => {
  return {
    id: 'default',
    free_shipping_threshold: 50.00,  // CHF 50
    max_weight: 30,                   // 30 kg
    max_dimensions: '100x100x100',    // 100×100×100 cm
    processing_days: 3,               // 3 dias
    active: true
  };
};

/**
 * Valida se o peso está dentro do limite
 * @param weight Peso total do carrinho (kg)
 * @returns true se válido
 */
export const validateWeight = async (weight: number): Promise<boolean> => {
  const settings = await getShippingSettings();
  return weight <= settings.max_weight;
};

/**
 * Parse dimensões do formato "LxWxH" para objeto
 * @param dimensionsStr String no formato "100x100x100"
 */
const parseDimensions = (dimensionsStr: string): { length: number; width: number; height: number } => {
  const parts = dimensionsStr.split('x').map(s => parseInt(s.trim()));
  return {
    length: parts[0] || 0,
    width: parts[1] || 0,
    height: parts[2] || 0
  };
};

/**
 * Valida se as dimensões estão dentro do limite
 * @param length Comprimento (cm)
 * @param width Largura (cm)
 * @param height Altura (cm)
 */
export const validateDimensions = async (
  length: number,
  width: number,
  height: number
): Promise<DimensionsValidation> => {
  const settings = await getShippingSettings();
  const maxDims = parseDimensions(settings.max_dimensions);

  const isValid = (
    length <= maxDims.length &&
    width <= maxDims.width &&
    height <= maxDims.height
  );

  return {
    length,
    width,
    height,
    isValid,
    maxDimensions: settings.max_dimensions
  };
};

/**
 * Verifica se o carrinho se qualifica para frete grátis
 * @param cartTotal Total do carrinho (CHF)
 */
export const qualifiesForFreeShipping = async (cartTotal: number): Promise<boolean> => {
  const settings = await getShippingSettings();
  return cartTotal >= settings.free_shipping_threshold;
};

/**
 * Retorna quanto falta para frete grátis
 * @param cartTotal Total do carrinho (CHF)
 */
export const amountUntilFreeShipping = async (cartTotal: number): Promise<number> => {
  const settings = await getShippingSettings();
  const remaining = settings.free_shipping_threshold - cartTotal;
  return remaining > 0 ? remaining : 0;
};

/**
 * Limpa o cache (útil após atualização no admin)
 */
export const clearSettingsCache = (): void => {
  cachedSettings = null;
  cacheTimestamp = 0;
};
